import { IonContent, IonFooter, IonHeader, IonImg, IonList, IonMenu, IonToolbar } from '@ionic/react';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { Button } from '@/components/Button';
import { Divider } from '@/components/Divider/Divider';
import type { IconName } from '@/components/Icon';
import { Modal } from '@/components/Modal/Modal';
import { NavItem } from '@/components/NavItem/NavItem';
import { ORYXLogo } from '@/components/ORYXLogo/ORYXLogo';
import { Popover } from '@/components/Popover/Popover';
import { ProfileModalContent } from '@/components/ProfileModal/ProfileModalContent';

import { isDevelopment, isProduction } from '@/utils/environment';
import { isRouteMatch } from '@/utils/isRouteMatch';
import { mergeClassNames } from '@/utils/mergeClassNames';

import { useAppVersion } from '@/hooks/useAppVersion';

import { useAuthContext } from '@/contexts/authContext';
import { useUserInfoContext } from '@/contexts/userInfoContext';

import QuickLinkBg from '@/assets/bg-pattern-sidebar.png';

import styles from './Sidebar.module.css';

import type { ComponentProps, PropsWithChildren } from 'react';

type SidebarProps = ComponentProps<typeof IonMenu>;

interface AppPage {
  title: string;
  url: string;
  icon?: IconName;
  identifier: string;
  status: string;
  showOn: 'development' | 'production';
}

const appPages: AppPage[] = [
  {
    title: 'Home',
    url: '/dashboard',
    icon: 'home',
    identifier: 'dashboard',
    status: 'active',
    showOn: 'production',
  },
  {
    title: 'Reports',
    url: '/reports',
    identifier: 'reports',
    icon: 'chart',
    status: 'active',
    showOn: 'production',
  },
  {
    title: 'Clients',
    url: '/clients',
    identifier: 'clients',
    icon: 'person',
    status: 'active',
    showOn: 'production',
  },
  {
    title: 'Testing',
    url: '/sensortesting',
    identifier: 'testing',
    icon: 'ball',
    status: 'active',
    showOn: 'development',
  },
  {
    title: 'Training Builder Tool',
    url: '/training-builder',
    identifier: 'trainingBuilder',
    icon: 'arm',
    status: 'disabled',
    showOn: 'development',
  },
  {
    title: 'Knowledge Base',
    url: '/docs',
    icon: 'education',
    identifier: 'docs',
    status: 'disabled',
    showOn: 'development',
  },
];

export const Sidebar = ({ children, ...props }: PropsWithChildren<SidebarProps>) => {
  const location = useLocation();
  const { userInfo } = useUserInfoContext();
  const { signOut } = useAuthContext();
  const history = useHistory();

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const appVersion = useAppVersion();

  const filteredPagesByEnvironment = appPages.filter((page) => {
    if (isProduction) {
      return page.showOn === 'production';
    }
    return page;
  });

  const sidebarHiddenPages = ['/reportsV1', '/reportsV2', '/OpenFilePage', '/session'];

  const shouldHideSidebar = useMemo(() => {
    return sidebarHiddenPages.some((page) => location.pathname.toLowerCase().includes(page.toLowerCase()));
  }, [location.pathname]);

  return (
    <IonMenu
      className={mergeClassNames(props.className, styles.sidebar, shouldHideSidebar && styles.hidden)}
      contentId='main'
      type='push'
      {...props}>
      <IonHeader className={mergeClassNames(styles.header, 'ion-no-border')}>
        <IonToolbar className={styles.toolbar}>
          <a className={styles.logo} href='/dashboard'>
            <ORYXLogo />
          </a>
        </IonToolbar>
        <Divider />
      </IonHeader>

      {/* Primary menu */}
      <IonContent>
        <IonList className={styles.menu}>
          {filteredPagesByEnvironment.map((page) => (
            <NavItem
              disabled={page.status === 'disabled'}
              icon={page.icon}
              isActive={isRouteMatch(page.url, location.pathname)}
              key={page.identifier}
              routerDirection='none'
              routerLink={page.url}>
              {page.title}
            </NavItem>
          ))}
        </IonList>
      </IonContent>

      <Divider />

      <IonFooter className={mergeClassNames(styles.footer, 'ion-no-border')}>
        {/* Language menu */}
        <IonList className={styles.menu}>
          <NavItem detail={true} icon='language' id='trigger-language-popover'>
            English
            <Popover
              alignment='end'
              className={styles.popover}
              dismissOnSelect={true}
              side='left'
              trigger='trigger-language-popover'
              triggerAction='click'>
              <IonList className={styles.popoverList}>
                <NavItem href='/dashboard'>English</NavItem>
                <Divider />
                <NavItem disabled href='/nl/dashboard'>
                  Nederlands
                </NavItem>
              </IonList>
            </Popover>
          </NavItem>
          {/* <NavItem icon='settings' href='/settings'> */}
          {isDevelopment && <NavItem icon='settings'>Settings</NavItem>}
        </IonList>

        {/* User menu */}
        <IonList className={styles.menu}>
          <NavItem detail={true} icon='person' id='trigger-user-popover' isUser>
            {userInfo.firstName ?? userInfo.lastName ?? 'User'}

            <Popover
              alignment='start'
              className={mergeClassNames(styles.popover, styles.popoverUser)}
              dismissOnSelect={true}
              side='top'
              trigger='trigger-user-popover'
              triggerAction='click'>
              <IonList className={styles.popoverList}>
                <NavItem onClick={() => setIsProfileModalOpen(true)}>Your profile</NavItem>
                <Divider />
                <NavItem
                  isDanger
                  onClick={() => {
                    signOut();
                    history.push('/');
                  }}>
                  Sign out
                </NavItem>
              </IonList>
            </Popover>
          </NavItem>
        </IonList>

        {/* Profile modal */}
        <Modal
          footer={
            <>
              <div className={styles.appVersion}>App Version: {appVersion || 'Loading...'}</div>
              <Button onClick={() => setIsProfileModalOpen(false)} variant='primary'>
                Close
              </Button>
            </>
          }
          header='Profile'
          isOpen={isProfileModalOpen}
          onDidDismiss={() => setIsProfileModalOpen(false)}>
          <ProfileModalContent />
        </Modal>

        <IonImg className={styles.background} src={QuickLinkBg} />
      </IonFooter>
    </IonMenu>
  );
};
